import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    X, Shield, Award, Tag, Ticket, CheckCircle, CreditCard,
    ArrowRight, Zap, Download, Sparkles
} from 'lucide-react';
import { FaWindows, FaApple, FaXbox } from 'react-icons/fa';
import { DiAndroid } from 'react-icons/di';
import { SiOculus, SiPlaystation, SiSteamdeck } from 'react-icons/si';
import { BsNintendoSwitch } from 'react-icons/bs';
import { TbDeviceVisionProFilled } from 'react-icons/tb';
import toast from 'react-hot-toast';
import gsap from 'gsap';
import { loadStripe } from '@stripe/stripe-js';

import { getGamePriceDetails } from '../../utils/gamePricing';
import { downloadInvoicePdf } from '../../services/invoiceService';
import {
    validatePromoCode,
    applyPromoCode,
    createStandardOrder,
    createStripeOrder,
    createStripePaymentIntent
} from '../../redux/slice/order.slice';
import { fetchActiveDiscounts, fetchPromoCodes } from '../../redux/slice/discount.slice';
import { fetchPublicGatewaySettings } from '../../redux/slice/gateway.slice';
import { fetchUserData, fetchUserLibrary, removeFromCart } from '../../redux/slice/user.slice';
import StripePaymentModal from './StripePaymentModal';
import LazyImage from '../common/LazyImage';

const PlatformIconMap = {
    windows: FaWindows,
    playstation: SiPlaystation,
    xbox: FaXbox,
    nintendo: BsNintendoSwitch,
    quest: SiOculus,
    android: DiAndroid,
    ios: FaApple,
    visionpro: TbDeviceVisionProFilled,
    steamdeck: SiSteamdeck
};

const PlatformColorMap = {
    windows: '#0078d7',
    playstation: '#3b82f6',
    xbox: '#107c10',
    nintendo: '#e60012',
    quest: '#ffffff',
    android: '#3ddc84',
    ios: '#a3aaae',
    visionpro: '#a855f7',
    steamdeck: '#60a5fa'
};

export default function BuyNowModal({ isOpen, onClose, game, selectedPlatform = 'windows' }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { user } = useSelector((s) => s.auth);
    const { discounts, promoCodes } = useSelector((s) => s.discount);
    const { publicSettings } = useSelector((s) => s.gateway || {});
    const { cart } = useSelector((s) => s.userSlice);

    const [promoInput, setPromoInput] = useState('');
    const [appliedPromo, setAppliedPromo] = useState(null);
    const [isApplyingPromo, setIsApplyingPromo] = useState(false);
    const [showOffersModal, setShowOffersModal] = useState(false);

    const [isProcessing, setIsProcessing] = useState(false);
    const [isFinalizingOrder, setIsFinalizingOrder] = useState(false);
    const [orderSuccess, setOrderSuccess] = useState(null);

    const [showStripeModal, setShowStripeModal] = useState(false);
    const [stripeClientSecret, setStripeClientSecret] = useState('');
    const [stripePromise, setStripePromise] = useState(null);

    const modalRef = useRef(null);
    const backdropRef = useRef(null);
    const successRef = useRef(null);
    const stripeKeyRef = useRef(null);
    const hasAnimatedSuccessRef = useRef(false);

    // Sync discounts & gateway settings
    useEffect(() => {
        if (isOpen) {
            dispatch(fetchActiveDiscounts());
            if (!publicSettings) {
                dispatch(fetchPublicGatewaySettings());
            }
            if (user) {
                dispatch(fetchPromoCodes());
            }
        }
    }, [isOpen, dispatch, publicSettings, user]);

    // Setup stripe
    useEffect(() => {
        if (publicSettings?.stripePublishableKey && stripeKeyRef.current !== publicSettings.stripePublishableKey) {
            stripeKeyRef.current = publicSettings.stripePublishableKey;
            setStripePromise(loadStripe(publicSettings.stripePublishableKey));
        }
    }, [publicSettings]);

    // Reset state on modal open/close
    useEffect(() => {
        if (isOpen) {
            setOrderSuccess(null);
            setIsFinalizingOrder(false);
            setAppliedPromo(null);
            setPromoInput('');
            setIsProcessing(false);
            hasAnimatedSuccessRef.current = false;

            // GSAP opening animation
            if (backdropRef.current && modalRef.current) {
                gsap.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3 });
                gsap.fromTo(modalRef.current, { opacity: 0, scale: 0.95, y: 15 }, { opacity: 1, scale: 1, y: 0, duration: 0.35, ease: 'power3.out' });
            }

            document.body.style.overflow = 'hidden';
        } else {
            hasAnimatedSuccessRef.current = false;
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    useEffect(() => {
        if ((orderSuccess || isFinalizingOrder) && successRef.current && !hasAnimatedSuccessRef.current) {
            hasAnimatedSuccessRef.current = true;
            gsap.fromTo(successRef.current,
                { opacity: 0, scale: 0.9, y: 20 },
                { opacity: 1, scale: 1, y: 0, duration: 0.45, ease: 'back.out(1.5)' }
            );
        }
    }, [orderSuccess, isFinalizingOrder]);

    // Close with smooth animation
    const handleCloseModal = () => {
        const target = successRef.current || modalRef.current;
        if (backdropRef.current && target) {
            gsap.to(backdropRef.current, { opacity: 0, duration: 0.2 });
            gsap.to(target, {
                opacity: 0,
                scale: 0.95,
                y: 12,
                duration: 0.2,
                ease: 'power2.in',
                onComplete: () => {
                    onClose();
                }
            });
        } else {
            onClose();
        }
    };

    if (!isOpen || !game) return null;

    // Pricing details for single product
    const platform = (selectedPlatform || 'windows').toLowerCase();
    const details = getGamePriceDetails(game, platform, discounts);
    const unitPrice = details.price;
    const originalPrice = details.originalPrice;
    const discountPercent = details.discount;

    const taxRatePercent = publicSettings?.taxRate ?? 10;
    const tax = Number((unitPrice * (taxRatePercent / 100)).toFixed(2));
    const promoDiscountAmount = appliedPromo ? Number(appliedPromo.discountAmount || 0) : 0;
    const total = Math.max(0, Number((unitPrice + tax - promoDiscountAmount).toFixed(2)));

    const isPurchasingRestricted = () => {
        if (!publicSettings) return false;
        if (publicSettings.purchasePaused) {
            if (publicSettings.purchaseStartsAt) {
                const startsAt = new Date(publicSettings.purchaseStartsAt).getTime();
                return startsAt > Date.now();
            }
            return true;
        }
        return false;
    };

    const restricted = isPurchasingRestricted();

    const PlatformIcon = PlatformIconMap[platform] || FaWindows;
    const platformColor = PlatformColorMap[platform] || '#0078d7';

    const singleCheckoutItem = [{
        game: game._id || game.id,
        quantity: 1,
        platform: platform
    }];

    const populatedItemForInvoice = [{
        game: game,
        quantity: 1,
        platform: platform,
        price: unitPrice
    }];

    // Cleanup specific item from user's cart if present
    const cleanupCartIfPresent = () => {
        const gameId = (game._id || game.id).toString();
        const isInCart = cart.some(c => {
            const cGameId = (c.game?._id || c.game?.id || c.game || '').toString();
            const cPlat = (c.platform || 'windows').toLowerCase();
            return cGameId === gameId && cPlat === platform;
        });
        if (isInCart) {
            dispatch(removeFromCart({ gameId: game._id || game.id, platform }));
        }
        if (user?._id || user?.id) {
            dispatch(fetchUserData(user._id || user.id));
            dispatch(fetchUserLibrary(user._id || user.id));
        }
    };

    // Promo code apply handler
    const handleApplyPromoCode = async (codeToApply) => {
        const code = (codeToApply || promoInput).trim();
        if (!code) {
            toast.error('Please enter a promo code');
            return;
        }

        setIsApplyingPromo(true);
        try {
            await dispatch(validatePromoCode({
                code,
                orderValue: unitPrice
            })).unwrap();

            const promoRes = await dispatch(applyPromoCode({
                code,
                orderValue: unitPrice,
                items: singleCheckoutItem
            })).unwrap();

            setAppliedPromo(promoRes);
            setPromoInput(code);
            toast.success(`Promo code ${code} applied successfully!`);
        } catch (err) {
            toast.error(err || 'Invalid or expired promo code');
            setAppliedPromo(null);
        } finally {
            setIsApplyingPromo(false);
        }
    };

    const handleRemovePromo = () => {
        setAppliedPromo(null);
        setPromoInput('');
        toast.success('Promo code removed');
    };

    // Stripe Flow
    const handleStripePayment = async () => {
        if (!user) {
            toast.error('Please login to proceed with purchase');
            handleCloseModal();
            navigate('/login');
            return;
        }

        setIsProcessing(true);
        try {
            const secret = await dispatch(createStripePaymentIntent({
                items: singleCheckoutItem,
                promoCode: appliedPromo?.discountCode
            })).unwrap();

            setStripeClientSecret(secret);
            setShowStripeModal(true);
        } catch (error) {
            console.error('Stripe Error:', error);
            toast.error(error || 'Error initiating payment.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleStripeSuccess = async (paymentIntent) => {
        setShowStripeModal(false);
        setIsFinalizingOrder(true);
        try {
            const orderPayload = {
                items: singleCheckoutItem,
                promoCode: appliedPromo?.discountCode,
                stripePaymentIntentId: paymentIntent.id
            };

            const verifyRes = await dispatch(createStripeOrder(orderPayload)).unwrap();
            if (verifyRes.success) {
                downloadInvoicePdf(verifyRes.order, user, populatedItemForInvoice);
                cleanupCartIfPresent();
                setOrderSuccess(verifyRes.order);
                toast.success('Order placed successfully!');
            } else {
                toast.error(verifyRes.message || 'Order creation failed');
                setIsFinalizingOrder(false);
            }
        } catch (error) {
            toast.error(error || 'Order creation failed. Contact support.');
            setIsFinalizingOrder(false);
        } finally {
            setIsProcessing(false);
        }
    };

    // Free Checkout Flow
    const handleFreeCheckout = async () => {
        if (!user) {
            toast.error('Please login to proceed with checkout');
            handleCloseModal();
            navigate('/login');
            return;
        }

        setIsProcessing(true);
        setIsFinalizingOrder(true);
        try {
            const orderPayload = {
                items: singleCheckoutItem,
                promoCode: appliedPromo?.discountCode
            };

            const verifyRes = await dispatch(createStandardOrder(orderPayload)).unwrap();
            if (verifyRes.success) {
                downloadInvoicePdf(verifyRes.order, user, populatedItemForInvoice);
                cleanupCartIfPresent();
                setOrderSuccess(verifyRes.order);
                toast.success('Order placed successfully!');
            } else {
                toast.error(verifyRes.message || 'Order creation failed');
                setIsFinalizingOrder(false);
            }
        } catch (error) {
            toast.error(error || 'Order creation failed. Contact support.');
            setIsFinalizingOrder(false);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCheckoutSubmit = () => {
        if (restricted) return;
        if (total === 0) {
            handleFreeCheckout();
        } else {
            handleStripePayment();
        }
    };

    return (
        <>
            {/* SUCCESS VIEW (FRAMED MODAL MATCHING SISTER MODALS WITH CLOSE BUTTON) */}
            {(orderSuccess || isFinalizingOrder) ? (
                <div
                    ref={backdropRef}
                    onClick={orderSuccess ? handleCloseModal : undefined}
                    className="fixed inset-0 z-50 bg-[#04040a]/65 backdrop-blur-md flex items-center justify-center p-3 xs:p-4 sm:p-6 overflow-y-auto"
                >
                    <div
                        ref={successRef}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-[480px] rounded-2xl xs:rounded-3xl bg-[#090a16] border border-white/10 shadow-[0_0_60px_rgba(0,0,0,0.9),0_0_30px_rgba(16,231,96,0.15)] overflow-hidden p-6 xs:p-8 flex flex-col items-center text-center my-auto"
                    >
                        {/* Glowing Top Neon Bar */}
                        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-[#10e760] via-[#00f0ff] to-[#10e760] shadow-[0_0_15px_#10e760] z-20"></div>
                        <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#10e760]/10 rounded-full blur-3xl pointer-events-none"></div>
                        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-cyber-cyan/10 rounded-full blur-3xl pointer-events-none"></div>

                        {/* Top-right Close Button */}
                        <button
                            type="button"
                            onClick={handleCloseModal}
                            className="absolute top-3.5 right-3.5 xs:top-4 xs:right-4 p-1.5 xs:p-2 rounded-xl bg-white/5 border border-white/10 hover:border-cyber-pink/40 hover:bg-cyber-pink/15 text-gray-400 hover:text-cyber-pink transition-all z-20"
                            aria-label="Close"
                        >
                            <X className="w-4 h-4" />
                        </button>

                        {/* Green Glowing Square Icon */}
                        <div className="w-16 h-16 xs:w-20 xs:h-20 rounded-2xl bg-gradient-to-b from-[#10e760] via-[#00f5a0] to-[#00e676] flex items-center justify-center mx-auto mb-5 shadow-[0_0_40px_rgba(16,231,96,0.35)] shrink-0 mt-2">
                            <CheckCircle className="w-8 h-8 xs:w-10 xs:h-10 text-white stroke-[2.5]" />
                        </div>

                        {/* ORDER CONFIRMED */}
                        <p className="text-[10px] xs:text-[11px] font-cyber font-bold text-[#00ff87] uppercase tracking-[0.25em] mb-2">
                            ORDER CONFIRMED
                        </p>

                        {/* PAYMENT SUCCESSFUL */}
                        <h2 className="font-cyber font-bold text-2xl xs:text-3xl text-white uppercase tracking-wider mb-2.5 leading-tight">
                            PAYMENT SUCCESSFUL
                        </h2>

                        {/* Description */}
                        <p className="text-gray-400 text-xs xs:text-sm font-sans mb-7 leading-relaxed max-w-sm mx-auto">
                            Your games have been added to your library. Check your email for the receipt and download links.
                        </p>

                        {/* Action Buttons */}
                        <div className="w-full space-y-3">
                            <button
                                type="button"
                                onClick={() => {
                                    onClose();
                                    navigate('/library');
                                }}
                                className="w-full py-3 xs:py-3.5 sm:py-4 rounded-xl border border-[#00f0ff] bg-transparent hover:bg-[#00f0ff]/10 text-[#00f0ff] font-cyber font-bold text-xs uppercase tracking-widest transition-all shadow-[0_0_20px_rgba(0,240,255,0.2)] cursor-pointer"
                            >
                                VIEW MY LIBRARY
                            </button>

                            <button
                                type="button"
                                onClick={() => {
                                    onClose();
                                    navigate('/');
                                }}
                                className="w-full py-3 xs:py-3.5 sm:py-4 rounded-xl bg-white/[0.04] border border-white/10 hover:border-white/20 text-gray-400 hover:text-white font-cyber font-semibold text-xs uppercase tracking-wider transition-all cursor-pointer"
                            >
                                BACK TO HOME
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                /* INSTANT CHECKOUT VIEW */
                <div
                    ref={backdropRef}
                    onClick={handleCloseModal}
                    className={`fixed inset-0 z-50 bg-[#04040a]/65 backdrop-blur-md transition-opacity duration-300 flex items-center justify-center p-2.5 xs:p-4 sm:p-6 overflow-y-auto ${showStripeModal ? 'invisible pointer-events-none' : ''}`}
                >
                    {/* Modal Container */}
                    <div
                        ref={modalRef}
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-[520px] max-h-[92dvh] flex flex-col rounded-2xl xs:rounded-3xl bg-[#090a16] border border-white/10 shadow-[0_0_60px_rgba(0,0,0,0.9),0_0_30px_rgba(189,0,255,0.15)] overflow-hidden my-auto"
                    >
                        {/* Glowing Top Rainbow Bar */}
                        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-cyber-blue via-cyber-cyan to-cyber-purple shadow-[0_0_15px_#00f0ff] z-20"></div>
                        <div className="absolute -top-24 -right-24 w-60 h-60 bg-cyber-cyan/10 rounded-full blur-3xl pointer-events-none"></div>
                        <div className="absolute -bottom-24 -left-24 w-60 h-60 bg-cyber-purple/10 rounded-full blur-3xl pointer-events-none"></div>

                        <div className="flex flex-col max-h-[92dvh] overflow-hidden">
                            {/* Modal Header */}
                            <div className="shrink-0 px-4 py-3 xs:px-5 xs:py-3.5 sm:px-6 sm:py-4 border-b border-white/10 flex items-center justify-between bg-[#090a16]/80 backdrop-blur-sm z-10">
                                <div className="flex items-center gap-2.5 min-w-0">
                                    <div className="w-7 h-7 xs:w-8 xs:h-8 rounded-lg xs:rounded-xl bg-cyber-purple/20 border border-cyber-purple/40 flex items-center justify-center text-cyber-purple shadow-[0_0_12px_rgba(189,0,255,0.25)] shrink-0">
                                        <CreditCard className="w-3.5 h-3.5 xs:w-4 xs:h-4 text-cyber-purple" />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="font-cyber font-black text-sm xs:text-base sm:text-lg text-white uppercase tracking-wider truncate">
                                            Instant Checkout
                                        </h3>
                                        <p className="text-[9px] xs:text-[10px] font-cyber text-gray-400 tracking-wide truncate">
                                            Direct Card Payment via Stripe
                                        </p>
                                    </div>
                                </div>

                                <button
                                    onClick={handleCloseModal}
                                    className="p-1.5 xs:p-2 rounded-lg xs:rounded-xl bg-white/5 border border-white/10 hover:border-cyber-pink/40 hover:bg-cyber-pink/15 text-gray-400 hover:text-cyber-pink transition-all shrink-0 ml-2"
                                    aria-label="Close"
                                >
                                    <X className="w-3.5 h-3.5 xs:w-4 xs:h-4" />
                                </button>
                            </div>

                            {/* Scrollable Content Body */}
                            <div className="flex-1 overflow-y-auto overflow-x-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden px-4 py-3.5 xs:px-5 xs:py-4 sm:px-6 sm:py-5 space-y-3.5 xs:space-y-4 sm:space-y-5">
                                {/* Product Info Card */}
                                <div className="relative rounded-xl xs:rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] p-2.5 xs:p-3 sm:p-3.5 flex items-center gap-3 xs:gap-3.5 group hover:border-cyber-cyan/30 transition-all">
                                    <LazyImage
                                        src={game.cover_image?.url || game.cover_image || game.image?.url || game.image}
                                        placeholder={game.cover_image?.lowUrl || game.image?.lowUrl}
                                        alt={game.title}
                                        className="w-full h-full object-cover"
                                        containerClassName="w-16 h-16 min-[380px]:w-20 min-[380px]:h-20 sm:w-22 sm:h-22 rounded-xl overflow-hidden flex-shrink-0 border border-white/10"
                                    />

                                    <div className="flex-1 min-w-0 space-y-1">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span
                                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] xs:text-[10px] font-cyber font-bold uppercase tracking-wider"
                                                style={{
                                                    backgroundColor: `${platformColor}20`,
                                                    borderColor: `${platformColor}50`,
                                                    borderWidth: 1,
                                                    color: platformColor
                                                }}
                                            >
                                                <PlatformIcon className="w-2.5 h-2.5" />
                                                {platform}
                                            </span>
                                            <span className="text-[9.5px] xs:text-[10px] text-gray-400 font-cyber truncate max-w-[120px]">
                                                {game.category?.name || 'Action'}
                                            </span>
                                        </div>

                                        <h4 className="font-cyber font-bold text-xs min-[380px]:text-sm sm:text-base text-white tracking-wide truncate">
                                            {game.title}
                                        </h4>

                                        <div className="flex items-baseline gap-1.5 xs:gap-2 pt-0.5 flex-wrap">
                                            <span className="text-cyber-cyan font-cyber font-black text-sm xs:text-base sm:text-lg">
                                                ${unitPrice.toFixed(2)}
                                            </span>
                                            {discountPercent > 0 && (
                                                <>
                                                    <span className="text-[10px] xs:text-xs text-gray-500 line-through font-mono">
                                                        ${originalPrice.toFixed(2)}
                                                    </span>
                                                    <span className="text-cyber-green text-[8.5px] xs:text-[9.5px] font-cyber font-black px-1.5 py-0.2 rounded bg-cyber-green/15 border border-cyber-green/30">
                                                        -{discountPercent}% OFF
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Promo Code & Available Offers */}
                                <div className="space-y-2">
                                    {user && promoCodes?.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setShowOffersModal(true)}
                                            className="w-full py-2 rounded-xl bg-cyber-purple/10 hover:bg-cyber-purple/20 border border-cyber-purple/30 hover:border-cyber-purple/50 text-cyber-purple hover:text-white text-[9.5px] xs:text-[10px] font-cyber font-bold uppercase tracking-widest transition-all flex items-center justify-center gap-1.5"
                                        >
                                            <Ticket className="w-3.5 h-3.5" />
                                            <span>View Available Offers ({promoCodes.length})</span>
                                        </button>
                                    )}

                                    {appliedPromo ? (
                                        <div className="flex items-center justify-between p-2.5 xs:p-3 rounded-xl bg-cyber-green/10 border border-cyber-green/30">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <Ticket className="w-4 h-4 text-cyber-green shrink-0" />
                                                <div className="min-w-0">
                                                    <span className="text-cyber-green font-cyber font-black text-xs uppercase tracking-wider block truncate">
                                                        {appliedPromo.discountCode}
                                                    </span>
                                                    <span className="text-[9.5px] xs:text-[10px] text-gray-400 font-cyber block">
                                                        Saved ${promoDiscountAmount.toFixed(2)}
                                                    </span>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={handleRemovePromo}
                                                className="text-gray-400 hover:text-cyber-pink text-[11px] font-cyber font-bold transition-colors uppercase px-2 py-1 rounded bg-white/5 shrink-0 ml-2"
                                            >
                                                REMOVE
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex gap-2">
                                            <div className="relative flex-1 min-w-0">
                                                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
                                                <input
                                                    type="text"
                                                    placeholder="PROMO CODE"
                                                    value={promoInput}
                                                    onChange={(e) => setPromoInput(e.target.value)}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleApplyPromoCode()}
                                                    className="w-full pl-9 pr-2.5 py-2 xs:py-2.5 rounded-xl text-xs font-cyber text-white placeholder-gray-500 bg-white/[0.04] border border-white/10 focus:border-cyber-cyan/50 focus:outline-none uppercase tracking-wider transition-all"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleApplyPromoCode()}
                                                disabled={isApplyingPromo || !promoInput.trim()}
                                                className="px-3.5 xs:px-4 py-2 xs:py-2.5 rounded-xl bg-cyber-cyan/15 hover:bg-cyber-cyan/25 border border-cyber-cyan/40 hover:border-cyber-cyan text-cyber-cyan text-xs font-cyber font-bold uppercase tracking-wider transition-all disabled:opacity-40 disabled:pointer-events-none shrink-0"
                                            >
                                                {isApplyingPromo ? '...' : 'APPLY'}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Order Breakdown */}
                                <div className="rounded-xl xs:rounded-2xl p-3.5 xs:p-4 bg-white/[0.02] border border-white/10 space-y-2">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-gray-400 font-cyber">ITEM PRICE</span>
                                        <span className="text-white font-cyber font-medium">${unitPrice.toFixed(2)}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="text-gray-400 font-cyber">TAX ({taxRatePercent}%)</span>
                                        <span className="text-white font-cyber font-medium">${tax.toFixed(2)}</span>
                                    </div>
                                    {appliedPromo && (
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="text-cyber-green font-cyber">PROMO DISCOUNT</span>
                                            <span className="text-cyber-green font-cyber font-bold">-${promoDiscountAmount.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="pt-2 border-t border-white/10 flex items-center justify-between">
                                        <span className="text-xs font-cyber font-bold text-gray-300 uppercase tracking-widest">FINAL TOTAL</span>
                                        <span className="text-lg xs:text-xl sm:text-2xl font-cyber font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-100 to-cyber-cyan">
                                            ${total.toFixed(2)}
                                        </span>
                                    </div>
                                </div>

                                {/* Checkout Action Button */}
                                <div className="space-y-2 pt-1">
                                    {!user ? (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                handleCloseModal();
                                                navigate('/login');
                                            }}
                                            className="cyber-btn w-full !py-3 xs:!py-3.5 sm:!py-4 flex items-center justify-center gap-2 text-xs xs:text-sm"
                                        >
                                            <span>SIGN IN TO PURCHASE</span>
                                            <ArrowRight className="w-4 h-4" />
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={handleCheckoutSubmit}
                                            disabled={isProcessing || restricted}
                                            className="cyber-btn w-full !py-3 xs:!py-3.5 sm:!py-4 flex items-center justify-center gap-2 disabled:opacity-50 disabled:pointer-events-none relative overflow-hidden text-xs xs:text-sm"
                                        >
                                            {isProcessing ? (
                                                <span className="animate-pulse">PROCESSING PAYMENT...</span>
                                            ) : restricted ? (
                                                'PURCHASE PAUSED'
                                            ) : total === 0 ? (
                                                <>
                                                    <Sparkles className="w-4 h-4" />
                                                    <span>CLAIM GAME FOR FREE</span>
                                                </>
                                            ) : (
                                                <>
                                                    <CreditCard className="w-4 h-4 shrink-0" />
                                                    <span>PAY ${total.toFixed(2)}</span>
                                                    <ArrowRight className="w-4 h-4 shrink-0" />
                                                </>
                                            )}
                                        </button>
                                    )}

                                    {/* Trust Badges */}
                                    <div className="flex flex-wrap items-center justify-center gap-2 xs:gap-3.5 text-[9px] xs:text-[10px] text-gray-500 font-cyber pt-1">
                                        <span className="flex items-center gap-1">
                                            <Shield className="w-3 h-3 text-cyber-cyan shrink-0" /> Stripe 256-bit Encrypted
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Award className="w-3 h-3 text-cyber-cyan shrink-0" /> Instant Activation
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Zap className="w-3 h-3 text-cyber-cyan shrink-0" /> Region Free
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Available Offers Sub-Modal */}
            {showOffersModal && (
                <div className="fixed inset-0 z-[60] bg-[#04040a]/85 backdrop-blur-md flex items-center justify-center p-3 xs:p-4">
                    <div className="relative w-full max-w-md max-h-[85dvh] flex flex-col rounded-2xl border border-white/10 bg-[#0c0c18] p-4 xs:p-5 space-y-3.5 shadow-[0_0_50px_rgba(0,0,0,0.8)] overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-cyber-blue via-cyber-cyan to-cyber-purple"></div>

                        <div className="flex items-start justify-between shrink-0">
                            <div>
                                <h3 className="font-cyber font-bold text-sm xs:text-base text-white uppercase tracking-wider flex items-center gap-2">
                                    <Ticket className="w-4 h-4 text-cyber-purple" />
                                    Available Offers
                                </h3>
                                <p className="text-gray-400 text-[11px] xs:text-xs font-sans mt-0.5">
                                    Select a coupon to apply to your purchase.
                                </p>
                            </div>
                            <button
                                onClick={() => setShowOffersModal(false)}
                                className="p-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-cyber-pink/40 text-gray-400 hover:text-cyber-pink"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div data-lenis-prevent className="flex-1 overflow-y-auto space-y-2.5 pr-1">
                            {promoCodes.map((promo) => {
                                const isApplied = appliedPromo && appliedPromo.discountCode === promo.code;
                                const canApply = unitPrice >= (promo.minOrderValue || 0);

                                return (
                                    <div
                                        key={promo._id}
                                        onClick={() => {
                                            if (canApply && !isApplied) {
                                                handleApplyPromoCode(promo.code);
                                                setShowOffersModal(false);
                                            }
                                        }}
                                        className={`relative p-2.5 xs:p-3 rounded-xl border transition-all ${isApplied
                                            ? 'border-cyber-green bg-cyber-green/10'
                                            : canApply
                                                ? 'border-white/10 bg-white/[0.02] hover:border-cyber-purple/50 hover:bg-white/[0.05] cursor-pointer'
                                                : 'border-white/5 bg-white/[0.01] opacity-50 cursor-not-allowed'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-cyber font-bold text-xs text-white uppercase tracking-wider">
                                                {promo.code}
                                            </span>
                                            <span className="text-[9.5px] xs:text-[10px] font-cyber font-bold px-2 py-0.5 rounded bg-cyber-purple/20 text-cyber-purple border border-cyber-purple/30">
                                                {promo.discountType === 'percentage' ? `${promo.discountValue}% OFF` : `$${promo.discountValue} OFF`}
                                            </span>
                                        </div>
                                        <p className="text-[10.5px] xs:text-[11px] text-gray-400 mt-1">
                                            {promo.description || `Min order $${promo.minOrderValue || 0}`}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Stripe Payment Modal */}
            {showStripeModal && (
                <StripePaymentModal
                    isOpen={showStripeModal}
                    onClose={() => setShowStripeModal(false)}
                    amount={total}
                    clientSecret={stripeClientSecret}
                    stripePromise={stripePromise}
                    onSuccess={handleStripeSuccess}
                />
            )}
        </>
    );
}
